import { rgb, StandardFonts } from 'pdf-lib';

import { inspectSummary } from './pdfSummary.js';
import { cleanFacility } from './repText.js';

const generic =
  /^(?:AECG|ECG|HR|Holter|3-Lead|Name:|Patient:|Start Date:|Report Generated:|Version:)/i;
export function inspectFacility(pdf) {
  const candidates = new Map();
  for (let index = 0; index < Math.min(5, pdf.getPageCount()); index += 1) {
    const page = pdf.getPage(index);
    const lines = inspectSummary(pdf, index).lines;
    // Generated HR pages are not evidence for the original customer heading.
    if (lines.some((line) => /^HR\s*-\s*Study ID:/i.test(line.sentence.trim()))) continue;
    const top = lines.filter((line) => line.y > page.getHeight() - 40);
    const first = top.find((line) => line.sentence.trim());
    if (!first) continue;
    const value = cleanFacility(first.sentence);
    if (!value || generic.test(value) || value.length < 3) continue;
    const key = value.replace(/\s+/g, ' ').toLowerCase();
    const candidate = candidates.get(key) || { value, pages: 0, arcs: false };
    candidate.pages += 1;
    candidate.arcs ||= top.some((line) => /^3-Lead Standard$/i.test(line.sentence.trim()));
    candidates.set(key, candidate);
  }
  const valid = [...candidates.values()].filter(
    (candidate) => candidate.arcs || candidate.pages >= 2
  );
  if (valid.length > 1)
    throw new Error(
      'Customer headers disagree across source PDF pages. Check the selected PDF before inserting.'
    );
  return valid[0]?.value || '';
}

// Correct the generated page itself, so both manual and automatic insertion use
// the source PDF heading even if old REP metadata is still cached in the browser.
export async function alignFacilityHeader(hr, source, pageIndex = 0) {
  const facility = inspectFacility(source);
  await renameHrTitle(hr, pageIndex);
  const info = inspectSummary(hr, pageIndex);
  const page = hr.getPage(pageIndex);
  const line = info.lines.find(
    (item) => Math.abs(item.y - (page.getHeight() - 31)) < 1 && item.fontSize === 11
  );
  if (!line)
    throw new Error('Generated HR customer heading could not be located. Regenerate the HR page.');
  const replacement = facility || cleanFacility(line.sentence) || 'HR Study';
  if (replacement === line.sentence) return;
  const font = await hr.embedFont(StandardFonts.Helvetica);
  let size = line.fontSize;
  const width = page.getWidth() - 102;
  size = Math.min(size, width / font.widthOfTextAtSize(replacement, 1));
  const stream = info.streams[line.streamIndex];
  const changed = stream.text.slice(0, line.start) + stream.text.slice(line.end);
  const ref = hr.context.getObjectRef(stream.stream);
  if (!ref) throw new Error('Generated HR text stream cannot be updated.');
  hr.context.assign(
    ref,
    hr.context.flateStream(Uint8Array.from(changed, (character) => character.charCodeAt(0)))
  );
  page.drawText(replacement, {
    x: (page.getWidth() - font.widthOfTextAtSize(replacement, size)) / 2,
    y: line.y,
    size,
    font,
    color: rgb(0, 0, 0),
  });
}

// Rename legacy generated reports without changing the original ARCS strip pages.
export async function renameHrTitle(pdf, pageIndex = 0) {
  const info = inspectSummary(pdf, pageIndex);
  const page = pdf.getPage(pageIndex);
  const line = info.lines.find(
    (item) =>
      Math.abs(item.y - (page.getHeight() - 47)) < 1 &&
      /^(ECG Chart Report|HR Study)$/.test(item.sentence)
  );
  if (!line) return;
  const stream = info.streams[line.streamIndex];
  const changed = stream.text.slice(0, line.start) + stream.text.slice(line.end);
  const ref = pdf.context.getObjectRef(stream.stream);
  if (!ref) throw new Error('Generated HR title stream cannot be updated.');
  pdf.context.assign(
    ref,
    pdf.context.flateStream(Uint8Array.from(changed, (character) => character.charCodeAt(0)))
  );
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const title = 'HR Trend Report';
  page.drawText(title, {
    x: (page.getWidth() - font.widthOfTextAtSize(title, 11)) / 2,
    y: line.y,
    size: 11,
    font,
    color: rgb(0, 0, 0),
  });
}
